import { z } from 'zod'

import {
  accept,
  isPlayersTurn,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type TurnState,
} from '../engine.js'
import type { Rng } from '../rng.js'
import { pickWord, READING_BANDS, wordsIn, type ReadingBand } from '../words.js'

/**
 * Small Rabbit's Scramble.
 *
 * Shares DOL-14's word lists and reading bands with Hangman, which is what
 * makes it the cheapest of the four games — it is a different question about
 * the same content.
 */

/** How many arrangements are dealt up front, for the shuffle button. */
const ARRANGEMENTS = 6

/**
 * How hard the reducer tries for an arrangement that is not too close to the
 * answer before settling for one that merely differs from it.
 */
const SCRAMBLE_ATTEMPTS = 40

export interface ScrambleConfig {
  readonly band: ReadingBand
  /** Words already used this sitting, so a session does not repeat itself. */
  readonly exclude?: ReadonlySet<string>
  readonly startedAt: number
}

export interface ScrambleGuess {
  readonly guess: string
  readonly correct: boolean
}

export interface ScrambleState {
  readonly word: string
  readonly hint: string
  readonly band: ReadingBand
  /**
   * The arrangements the shuffle button cycles through.
   *
   * Dealt up front rather than shuffled on demand, because a reducer has no
   * randomness — and giving it some would mean a round could not be replayed,
   * which is what stops a score being whatever the client claims.
   */
  readonly arrangements: readonly string[]
  readonly arrangement: number
  readonly status: 'playing' | 'won'
  /** Wrong answers. Not lives: being wrong here costs nothing but a moment. */
  readonly wrongGuesses: number
  readonly hintShown: boolean
  /** What she actually typed, which may not be the word that was dealt. */
  readonly solvedWith: string | null
  readonly lastGuess: ScrambleGuess | null
  readonly turn: TurnState
  readonly startedAt: number
}

export const scrambleAction = z.union([
  z.object({ type: z.literal('guess'), word: z.string().min(1).max(32) }),
  z.object({ type: z.literal('shuffle') }),
  z.object({ type: z.literal('hint') }),
])

export type ScrambleAction = z.infer<typeof scrambleAction>

export interface ScrambleView {
  readonly letters: readonly string[]
  readonly band: ReadingBand
  /** Null until she asks for it. Being stuck should not just be losing slowly. */
  readonly hint: string | null
  readonly hintShown: boolean
  readonly status: ScrambleState['status']
  readonly wrongGuesses: number
  readonly lastGuess: ScrambleGuess | null
  /** Null while playing; the word she found once the round is over. */
  readonly answer: string | null
  readonly currentPlayerId: string | null
}

/** Letters in order, so two words made of the same letters share a key. */
function letterKey(word: string): string {
  return [...word].sort().join('')
}

/**
 * Every word in every band, grouped by the letters it is made of.
 *
 * Built once. It is what lets a genuinely correct answer count even when it is
 * not the word that was dealt: unjumbling LEMON into MELON is not a mistake,
 * and telling a child she is wrong when she is right is the fastest way to make
 * her stop trusting the game.
 */
let anagramIndex: Map<string, Set<string>> | undefined

function anagramsOf(word: string): ReadonlySet<string> {
  if (!anagramIndex) {
    anagramIndex = new Map()

    for (const band of READING_BANDS) {
      for (const entry of wordsIn(band)) {
        const key = letterKey(entry.word)
        const found = anagramIndex.get(key)
        if (found) found.add(entry.word)
        else anagramIndex.set(key, new Set([entry.word]))
      }
    }
  }

  return anagramIndex.get(letterKey(word)) ?? new Set([word])
}

/** How many positions two arrangements of the same letters disagree in. */
function distance(a: string, b: string): number {
  let moved = 0
  for (let index = 0; index < a.length; index++) if (a[index] !== b[index]) moved += 1
  return moved
}

/**
 * How scrambled a word has to look.
 *
 * Half its letters, so a five-letter word cannot come up with one pair swapped
 * and call itself a puzzle. Short words are held to two, which is every
 * rearrangement a three-letter word has that is not the word itself.
 */
function minimumDistance(length: number): number {
  return Math.min(length, Math.max(2, Math.ceil(length / 2)))
}

/**
 * Deals the arrangements the shuffle button cycles through.
 *
 * None of them is ever the answer. A one-in-N chance of being handed the word
 * you were asked to unjumble looks like a bug and feels like one, and it is
 * exactly the sort of thing that happens rarely enough to ship.
 */
function scramble(word: string, rng: Rng): readonly string[] {
  const target = minimumDistance(word.length)
  const good: string[] = []
  const passable: string[] = []

  for (let attempt = 0; attempt < SCRAMBLE_ATTEMPTS && good.length < ARRANGEMENTS; attempt++) {
    const candidate = rng.shuffle([...word]).join('')
    if (candidate === word) continue

    if (distance(candidate, word) >= target) {
      if (!good.includes(candidate)) good.push(candidate)
    } else if (!passable.includes(candidate)) {
      passable.push(candidate)
    }
  }

  const arrangements = [...good, ...passable].slice(0, ARRANGEMENTS)
  if (arrangements.length > 0) return arrangements

  // Nothing random landed. Vanishingly unlikely for a word with two distinct
  // letters in it, but "unlikely" is not "cannot", and the one guarantee this
  // whole function exists to make is that the answer is never handed back.
  //
  // So the fallback is constructed rather than drawn: swapping the first
  // letter with the first one that differs from it always produces something
  // that is not the word. Words with only one distinct letter are excluded
  // before we get here, because they have no arrangement at all.
  return [swapFirstDifferent(word)]
}

/**
 * Moves the first letter past the first one unlike it.
 *
 * Guaranteed to differ from the word it was given, provided that word has two
 * distinct letters — which is what makes it usable as a last resort where a
 * reversal is not: a palindrome reverses to itself.
 */
function swapFirstDifferent(word: string): string {
  const letters = [...word]
  const other = letters.findIndex((letter) => letter !== letters[0])
  if (other === -1) return word

  const first = letters[0] as string
  letters[0] = letters[other] as string
  letters[other] = first

  return letters.join('')
}

/** A word with only one distinct letter has no arrangement but itself. */
function isScrambleable(word: string): boolean {
  return new Set(word).size >= 2
}

/** Tidies an answer before it is judged. Case and stray spaces are not the test. */
function normalise(guess: string): string {
  return guess.trim().toLowerCase().replace(/\s+/g, '')
}

export const scrambleEngine: GameEngine<ScrambleConfig, ScrambleState, ScrambleAction> = {
  key: 'scramble',
  actionSchema: scrambleAction,

  createInitialState(config, players, rng: Rng): ScrambleState {
    // Words that cannot be scrambled are excluded rather than handled further
    // down. There are none in the lists today, but a word list is data and data
    // changes, and "AAA" would otherwise have to be presented as itself — the
    // free word this game goes out of its way never to hand over.
    const unplayable = wordsIn(config.band)
      .filter((each) => !isScrambleable(each.word))
      .map((each) => each.word)

    const exclude = new Set([...(config.exclude ?? []), ...unplayable])
    const entry = pickWord(config.band, rng, { exclude })

    return {
      word: entry.word,
      hint: entry.hint,
      band: config.band,
      arrangements: scramble(entry.word, rng),
      arrangement: 0,
      status: 'playing',
      wrongGuesses: 0,
      hintShown: false,
      solvedWith: null,
      lastGuess: null,
      turn: { players, currentPlayerId: players[0]?.id ?? null },
      startedAt: config.startedAt,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')
    if (!isPlayersTurn(state.turn, context.playerId)) return reject(state, 'not-your-turn')

    if (action.type === 'shuffle') {
      // A fresh arrangement often unsticks you, and it costs nothing — this is
      // a puzzle, not a test.
      return accept({
        ...state,
        arrangement: (state.arrangement + 1) % state.arrangements.length,
      })
    }

    if (action.type === 'hint') {
      if (state.hintShown) return reject(state, 'already-played')
      return accept({ ...state, hintShown: true })
    }

    const guess = normalise(action.word)
    if (guess === '') return reject(state, 'invalid-action')

    // Any real word made of exactly these letters counts, not only the one that
    // was dealt. She unjumbled the letters; that was the question.
    const correct = anagramsOf(state.word).has(guess)

    if (!correct) {
      // Not fatal, and not a life lost. Being wrong here costs a moment.
      return accept({
        ...state,
        wrongGuesses: state.wrongGuesses + 1,
        lastGuess: { guess, correct: false },
      })
    }

    return accept({
      ...state,
      status: 'won',
      solvedWith: guess,
      lastGuess: { guess, correct: true },
      turn: { ...state.turn, currentPlayerId: null },
    })
  },

  isTerminal: (state) => state.status !== 'playing',

  summarise(state, endedAt): PlaySummary {
    const durationMs = Math.max(0, endedAt - state.startedAt)

    return {
      gameKey: 'scramble',
      result: state.status === 'won' ? 'won' : 'abandoned',
      durationMs,
      difficulty: state.band,
    }
  },
}

/**
 * Strips the answer before the state leaves the server.
 *
 * The same rule as Hangman: the word is in the state, so the state does not go
 * to a browser. The letters do, which is the whole puzzle.
 */
export function toScrambleView(state: ScrambleState): ScrambleView {
  const arrangement = state.arrangements[state.arrangement] ?? state.arrangements[0] ?? state.word

  return {
    letters: [...arrangement],
    band: state.band,
    hint: state.hintShown || state.status !== 'playing' ? state.hint : null,
    hintShown: state.hintShown,
    status: state.status,
    wrongGuesses: state.wrongGuesses,
    lastGuess: state.lastGuess,
    answer: state.status === 'playing' ? null : (state.solvedWith ?? state.word),
    currentPlayerId: state.turn.currentPlayerId,
  }
}
